import { z } from "zod";
import { isRuntimeProvider } from "./runtime.js";
/**
 * The canonical model catalog — the ONE web-safe list of the models the
 * platform runs agents on (Template Strategy V2, phase 1:
 * docs/TEMPLATE-STRATEGY-V2.md §4). Canonical ids are the only model
 * identifiers that appear in specs, policies, pricing and telemetry (owner
 * decision D5). Each row's `serving` map holds the provider-native names the
 * engines send on the wire (the Anthropic id, the OpenRouter slug, the Foundry
 * deployment): a model id may name its provider — `anthropic/<name>`,
 * `foundry/<name>`, `openrouter/<name>`, the name being a canonical id or that
 * provider's native name (owner decisions 2026-10-12) — and a bare id runs at
 * its home provider.
 *
 * Rows never disappear: a model leaves the platform by flipping `status` to
 * "retired" (stored specs that pin it must still parse and display), never by
 * deleting its row. Labels are V1's friendly names (the former web
 * MODEL_LABELS map).
 */
export const MODEL_FAMILIES = ["claude", "kimi", "glm", "deepseek", "gpt"];
export const MODEL_CATALOG = [
    {
        id: "claude-haiku-4-5",
        label: "Haiku 4.5",
        family: "claude",
        status: "active",
        homeProvider: "anthropic",
        serving: { anthropic: "claude-haiku-4-5", openrouter: "anthropic/claude-haiku-4.5" },
    },
    {
        id: "claude-haiku-5-5",
        label: "Haiku 5.5",
        family: "claude",
        status: "active",
        homeProvider: "anthropic",
        serving: { anthropic: "claude-haiku-5-5", openrouter: "anthropic/claude-haiku-5.5" },
    },
    {
        id: "claude-sonnet-5",
        label: "Sonnet 5",
        family: "claude",
        status: "active",
        homeProvider: "anthropic",
        serving: { anthropic: "claude-sonnet-5", openrouter: "anthropic/claude-sonnet-5" },
    },
    {
        id: "claude-sonnet-5-5",
        label: "Sonnet 5.5",
        family: "claude",
        status: "active",
        homeProvider: "anthropic",
        serving: { anthropic: "claude-sonnet-5-5", openrouter: "anthropic/claude-sonnet-5.5" },
    },
    {
        id: "claude-opus-4-8",
        label: "Opus 4.8",
        family: "claude",
        status: "active",
        homeProvider: "anthropic",
        serving: { anthropic: "claude-opus-4-8", openrouter: "anthropic/claude-opus-4.8" },
    },
    {
        id: "claude-opus-5",
        label: "Opus 5",
        family: "claude",
        status: "active",
        homeProvider: "anthropic",
        serving: { anthropic: "claude-opus-5", openrouter: "anthropic/claude-opus-5" },
    },
    {
        id: "claude-fable-5",
        label: "Fable 5",
        family: "claude",
        status: "active",
        homeProvider: "anthropic",
        serving: { anthropic: "claude-fable-5", openrouter: "anthropic/claude-fable-5" },
    },
    {
        id: "claude-sonnet-4-6",
        label: "Sonnet 4.6",
        family: "claude",
        status: "active",
        homeProvider: "anthropic",
        serving: { anthropic: "claude-sonnet-4-6", foundry: "claude-sonnet-4-6" },
    },
    {
        id: "kimi-k3",
        label: "Kimi K3",
        family: "kimi",
        status: "active",
        homeProvider: "openrouter",
        serving: { openrouter: "moonshotai/kimi-k3" },
    },
    {
        id: "kimi-k2-6",
        label: "Kimi K2.6",
        family: "kimi",
        status: "active",
        homeProvider: "foundry",
        serving: { foundry: "Kimi-K2.6", openrouter: "moonshotai/kimi-k2.6" },
    },
    {
        id: "kimi-k2-7-code",
        label: "Kimi K2.7 Code",
        family: "kimi",
        status: "active",
        homeProvider: "foundry",
        serving: { foundry: "Kimi-K2.7-Code", openrouter: "moonshotai/kimi-k2.7-code" },
    },
    {
        id: "glm-5-2",
        label: "GLM 5.2",
        family: "glm",
        status: "active",
        homeProvider: "openrouter",
        serving: { openrouter: "z-ai/glm-5.2" },
    },
    {
        id: "deepseek-v4",
        label: "DeepSeek V4",
        family: "deepseek",
        status: "active",
        homeProvider: "foundry",
        serving: { foundry: "DeepSeek-V4-Pro", openrouter: "deepseek/deepseek-v4-pro-0813" },
    },
    {
        id: "deepseek-v4-flash",
        label: "DeepSeek V4 Flash",
        family: "deepseek",
        status: "active",
        homeProvider: "foundry",
        serving: { foundry: "DeepSeek-V4-Flash" },
    },
    {
        id: "gpt-5-4-mini",
        label: "GPT-5.4 Mini",
        family: "gpt",
        status: "active",
        homeProvider: "foundry",
        serving: { foundry: "gpt-5.4-mini" },
    },
    {
        id: "gpt-5-6-sol",
        label: "GPT-5.6 Sol",
        family: "gpt",
        status: "active",
        homeProvider: "foundry",
        serving: { foundry: "gpt-5.6-sol" },
    },
];
export const CANONICAL_MODEL_IDS = MODEL_CATALOG.map((row) => row.id);
export function isCanonicalModelId(value) {
    return typeof value === "string" && CANONICAL_MODEL_IDS.includes(value);
}
/**
 * `<provider>/<name>` or a bare `<id>` (owner decisions 2026-10-12). The provider is the text
 * before the FIRST slash and must be one of the three; `<name>` is a catalog id OR that
 * provider's native name, so `openrouter/anthropic/claude-haiku-4.5`, `foundry/Kimi-K2.6` and
 * `openrouter/kimi-k3` are all ids. Only OpenRouter's names carry a slash (`owner/model`), so
 * only `openrouter/` may be followed by one more. A bare OpenRouter slug (`moonshotai/kimi-k3`)
 * is never an id. Null when malformed; `modelRefIssue` says why.
 */
export function parseModelRef(id) {
    const slash = id.indexOf("/");
    if (slash < 0)
        return id.length > 0 ? { provider: null, canonical: id, native: null } : null;
    const provider = id.slice(0, slash);
    const name = id.slice(slash + 1);
    if (!isRuntimeProvider(provider))
        return null;
    const parts = name.split("/");
    if (parts.some((part) => part.length === 0))
        return null;
    if (parts.length > (provider === "openrouter" ? 2 : 1))
        return null;
    const byId = MODEL_CATALOG.find((row) => row.id === name);
    if (byId)
        return { provider, canonical: byId.id, native: servingOf(byId)[provider] ?? null };
    const byNative = MODEL_CATALOG.find((row) => servingOf(row)[provider] === name);
    if (byNative)
        return { provider, canonical: byNative.id, native: name };
    return { provider, canonical: name, native: name };
}
/** The row whose OpenRouter slug is exactly `id` — the id someone meant to prefix with `openrouter/`. */
const openrouterSlugOwner = (id) => MODEL_CATALOG.find((row) => servingOf(row).openrouter === id);
/**
 * One sentence on why the id is not a model id, or null when it is one. An OpenRouter slug
 * written without its `openrouter/` prefix is refused with the id to write instead — both the
 * bare slug (`moonshotai/kimi-k3`) and a slug whose owner happens to be a provider
 * (`anthropic/claude-haiku-4.5` parses as Anthropic + an unknown name). A slug that spells a
 * catalog id under its owner (`anthropic/claude-opus-5`) is that provider's id.
 */
export function modelRefIssue(id) {
    if (id.length === 0)
        return "model is missing";
    const ref = parseModelRef(id);
    const known = ref !== null && MODEL_CATALOG.some((row) => row.id === ref.canonical);
    if (ref && (known || ref.provider === null || ref.provider === "openrouter"))
        return null;
    const slugOwner = openrouterSlugOwner(id);
    if (slugOwner)
        return `"${id}" is OpenRouter's name for ${slugOwner.label}: write openrouter/${id} (or openrouter/${slugOwner.id})`;
    if (ref)
        return null; // a name the catalog does not know (a deployment, a fixture): not refused here
    const first = id.slice(0, id.indexOf("/"));
    if (isRuntimeProvider(first))
        return `"${id}" is not a model id: after ${first}/ comes one name${first === "openrouter" ? " (an OpenRouter slug may be owner/model)" : ""}`;
    return `"${first}" is not a provider (anthropic | foundry | openrouter); a model id is <provider>/<model> or a bare model id (an OpenRouter slug is written openrouter/${id}).`;
}
/** A row's serving map at its declared type (the catalog literal narrows each row to its own keys). */
const servingOf = (row) => row.serving;
/** The canonical spelling of a ref: `<provider>/<catalog id>` (or the bare id). */
export const modelRefString = (ref) => ref.provider ? `${ref.provider}/${ref.canonical}` : ref.canonical;
/** Two ids name the same (provider, model) pair; a bare id counts at its home, a native name as its model. */
export function sameModelRef(a, b) {
    const ra = resolveModelRef(a);
    const rb = resolveModelRef(b);
    if (ra && rb)
        return ra.provider === rb.provider && ra.canonical === rb.canonical;
    return a === b;
}
/**
 * The pair an id names: its prefix, else the catalog home; null for a malformed or unknown bare
 * id. `native` is the name the provider knows it by (for a bare id, the home's serving entry).
 */
export function resolveModelRef(id) {
    const ref = parseModelRef(id);
    if (!ref)
        return null;
    if (ref.provider)
        return { provider: ref.provider, canonical: ref.canonical, native: ref.native };
    const home = homeProviderFor(ref.canonical);
    if (!home)
        return null;
    return { provider: home, canonical: ref.canonical, native: nativeModelName(home, ref.canonical) };
}
/** What the web writes into agent.yaml for a pair: bare at the model's home, prefixed elsewhere. */
export function authoredModelId(provider, canonical) {
    return homeProviderFor(canonical) === provider ? canonical : `${provider}/${canonical}`;
}
/** The name `provider` knows an active catalog model by, or null when it does not serve it. */
export function nativeModelName(provider, id) {
    const row = modelCatalogRow(id);
    return row && row.status === "active" ? (servingOf(row)[provider] ?? null) : null;
}
/** Every runnable (provider, model) pair of the active catalog, in catalog order, home first per row. */
export function servedPairs() {
    const out = [];
    for (const row of MODEL_CATALOG) {
        if (row.status !== "active")
            continue;
        const serving = servingOf(row);
        const providers = Object.keys(serving);
        providers.sort((a, b) => Number(b === row.homeProvider) - Number(a === row.homeProvider));
        for (const provider of providers) {
            const native = serving[provider];
            if (native)
                out.push({ provider, canonical: row.id, native, home: provider === row.homeProvider });
        }
    }
    return out;
}
/**
 * A model id as authored (`execution.models`, an advisor): `<provider>/<name>` or a bare id. The
 * name is a catalog id or that provider's native name; a name that is neither must be canonical
 * (lowercase kebab-case, no revision suffix).
 */
export const ModelIdSchema = z.string().superRefine((value, ctx) => {
    // A provider's native name (`foundry/gpt-5.4-mini`, `openrouter/z-ai/glm-5.2`) resolves to its
    // catalog row, so the kebab rule below reads the canonical id it names, never the native spelling.
    const issue = modelRefIssue(value);
    const ref = parseModelRef(value);
    if (issue || !ref) {
        ctx.addIssue({ code: "custom", message: issue ?? "not a model id" });
        return;
    }
    if (!/^[a-z0-9][a-z0-9-]*$/.test(ref.canonical))
        ctx.addIssue({
            code: "custom",
            message: "model ids are canonical (lowercase kebab-case), optionally prefixed by their provider, with no revision suffix",
        });
});
/** The catalog row for an id: bare, `<provider>/<id>`, or `<provider>/<native name>`. */
export function modelCatalogRow(id) {
    const canonical = parseModelRef(id)?.canonical ?? id;
    return MODEL_CATALOG.find((row) => row.id === canonical);
}
/** Friendly display name; unknown ids display as-is (V1's behaviour). */
export function modelLabel(id) {
    return modelCatalogRow(id)?.label ?? id;
}
/** Models NEW specs may pin and providers may serve, in catalog order. */
export function activeModelIds() {
    return MODEL_CATALOG.filter((row) => row.status === "active").map((row) => row.id);
}
// --- Model sets (Template Strategy V2 phase 4c) -------------------------------
/**
 * Registry-defined MODEL SETS — the grammar plan entitlements use instead of
 * literal model lists (owner decision 2026-08-21; design §11). A plan's
 * `models.allowed` entry is either a model id or `set:<id>`; sets expand when
 * the plan's entitlements become a policy layer (`planEntitlementsToLayer`),
 * so a NEW model joins every plan carrying its set by ONE catalog row (plus
 * registry serving + a pricing migration) — no plan-version roll, no offer
 * clone, no assignment roll, no Stripe sync. Migration 110 was the LAST roll.
 *
 * Tiers are cumulative; members are listed in catalog order and a RETIRED
 * model leaves every set automatically (status flip, no roll). Editing a set
 * is a code-reviewed change to what sealed plans effectively grant: the launch
 * manifest freezes the expanded policy, so in-flight runs are unaffected.
 */
export const MODEL_SET_IDS = ["essentials", "standard", "frontier"];
export const MODEL_SET_PREFIX = "set:";
const ESSENTIALS_MEMBERS = new Set([
    "claude-haiku-4-5",
    "claude-haiku-5-5",
    "claude-sonnet-5",
    "claude-sonnet-5-5",
    "kimi-k3",
    "glm-5-2",
    "deepseek-v4",
    "deepseek-v4-flash",
    "gpt-5-4-mini",
    "gpt-5-6-sol",
]);
const STANDARD_MEMBERS = new Set([
    ...ESSENTIALS_MEMBERS,
    "claude-opus-4-8",
    "claude-opus-5",
]);
const FRONTIER_MEMBERS = new Set([
    ...STANDARD_MEMBERS,
    "claude-fable-5",
]);
/** Catalog order, active rows only — a retired member is simply absent. */
function catalogOrdered(members) {
    return MODEL_CATALOG.filter((row) => row.status === "active" && members.has(row.id)).map((row) => row.id);
}
export const MODEL_SETS = {
    essentials: catalogOrdered(ESSENTIALS_MEMBERS),
    standard: catalogOrdered(STANDARD_MEMBERS),
    frontier: catalogOrdered(FRONTIER_MEMBERS),
};
export function isModelSetId(value) {
    return typeof value === "string" && MODEL_SET_IDS.includes(value);
}
/** `set:<known id>`; any other `set:`-prefixed string is an unknown set (fail closed). */
export function isModelSetRef(value) {
    return (typeof value === "string" &&
        value.startsWith(MODEL_SET_PREFIX) &&
        isModelSetId(value.slice(MODEL_SET_PREFIX.length)));
}
/**
 * Expand model refs IN PLACE: a set ref contributes its members (catalog
 * order, active only) where it appears; literal ids pass through verbatim —
 * an id unknown to the catalog is kept (it simply never matches a spec), so
 * legacy plan lists keep their meaning and order. Duplicates collapse to the
 * first occurrence. Throws on an unknown set ref: a plan naming a set the
 * registry does not define must refuse to compute a policy, never guess.
 */
export function expandModelRefs(refs) {
    const expanded = [];
    const seen = new Set();
    const push = (id) => {
        if (seen.has(id))
            return;
        seen.add(id);
        expanded.push(id);
    };
    for (const ref of refs) {
        if (ref.startsWith(MODEL_SET_PREFIX)) {
            const setId = ref.slice(MODEL_SET_PREFIX.length);
            if (!isModelSetId(setId))
                throw new Error(`model_set_unknown:${ref}`);
            for (const id of MODEL_SETS[setId])
                push(id);
        }
        else {
            push(ref);
        }
    }
    return expanded;
}
/** The home provider of a served model, or null for an unknown or retired id. */
export function homeProviderFor(id) {
    const row = modelCatalogRow(id);
    return row && row.status === "active" ? row.homeProvider : null;
}
/** The OpenRouter slug of an active model OpenRouter serves, or null (`nativeModelName` for the gateway). */
export const openrouterSlugFor = (id) => nativeModelName("openrouter", id);
//# sourceMappingURL=models.js.map